// src/pages/StudySchedulePage.js - 일정 확인 페이지

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faChevronLeft
} from '@fortawesome/free-solid-svg-icons';
import WorkspaceLayout from '../components/WorkspaceLayout';
import './Participants.scss';
import { usePageHeader } from '../context/PageHeaderContext';
import { apiFetch, apiUrl } from '../services/apiClient';

// SimpleCalendar 렌더링 컴포넌트 (Participants.js에서 렌더링 로직만 복사)
const CalendarView = ({ optimizedSchedule, participantsData, nameColumn, targetGroups, validationData }) => {
  // AI 최적화 결과 파싱 (Participants.js와 동일)
  const finalScheduleData = optimizedSchedule ? (() => {
    const aiSchedule = {};
    const scheduleAssignments = optimizedSchedule.schedule_assignments || {};
    
    Object.keys(scheduleAssignments).forEach(dateKey => {
      const dayAssignments = scheduleAssignments[dateKey];
      if (!dayAssignments || typeof dayAssignments !== 'object') return;
      
      const weekday = dayAssignments.weekday;
      
      aiSchedule[dateKey] = {
        date: dateKey,
        weekday: weekday,
        sessions: {}
      };
      
      // 시간대별 세션 정보 추출
      Object.keys(dayAssignments).forEach(timeSlot => {
        if (timeSlot === 'weekday') return;
        
        const participantNames = Array.isArray(dayAssignments[timeSlot]) 
          ? dayAssignments[timeSlot] 
          : [dayAssignments[timeSlot]];
        
        const firstParticipant = participantNames[0];
        if (firstParticipant && typeof firstParticipant === 'string') {
          const participantObj = participantsData.find(p => {
            const pName = p.participant_name || p[nameColumn] || '';
            return pName && String(pName).trim() === String(firstParticipant).trim();
          });
          
          if (participantObj) {
            aiSchedule[dateKey].sessions[timeSlot] = [{
              name: firstParticipant,
              participant: participantObj
            }];
          } else {
            aiSchedule[dateKey].sessions[timeSlot] = [{
              name: firstParticipant,
              participant: null
            }];
          }
        }
      });
    });
    
    return aiSchedule;
  })() : {};
  
  const scheduleDateKeys = Object.keys(finalScheduleData);
  
  if (scheduleDateKeys.length === 0) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ 
          background: '#fff3cd', 
          border: '1px solid #ffc107', 
          borderRadius: '8px',
          padding: '20px',
          color: '#856404'
        }}>
          <p style={{ marginBottom: '8px', fontWeight: 600 }}>
            ⚠️ 일정 데이터 없음
          </p>
          <p style={{ fontSize: '14px', color: '#666' }}>
            일정 데이터가 없습니다.
          </p>
        </div>
      </div>
    );
  }
  
  // 날짜 파싱 및 정렬 (Participants.js와 동일)
  const weekdayMap = { '일': 0, '월': 1, '화': 2, '수': 3, '목': 4, '금': 5, '토': 6 };
  
  const sortedDates = scheduleDateKeys
    .map(dateKey => {
      const [month, day] = dateKey.split('-').map(Number);
      const dayData = finalScheduleData[dateKey];
      
      return {
        dateKey: dateKey,
        month: month,
        day: day,
        weekday: dayData?.weekday || '',
        weekdayIndex: dayData?.weekday ? weekdayMap[dayData.weekday] : null,
        sessions: dayData?.sessions || {}
      };
    })
    .sort((a, b) => {
      if (a.month !== b.month) return a.month - b.month;
      return a.day - b.day;
    });
  
  // 주 단위로 그룹화
  const weeks = [];
  let currentWeek = new Array(7).fill(null);
  
  sortedDates.forEach((dateInfo) => {
    const weekdayIndex = dateInfo.weekdayIndex;
    
    if (weekdayIndex === null || weekdayIndex < 0 || weekdayIndex >= 7) {
      return;
    }
    
    if (currentWeek[weekdayIndex] === null) {
      currentWeek[weekdayIndex] = dateInfo;
    } else {
      weeks.push(currentWeek);
      currentWeek = new Array(7).fill(null);
      currentWeek[weekdayIndex] = dateInfo;
    }
  });
  
  if (currentWeek.some(day => day !== null)) {
    weeks.push(currentWeek);
  }
  
  const weekdayHeaders = ['일', '월', '화', '수', '목', '금', '토'];
  
  // 그룹별 색상
  const groupColors = [
    { bg: '#e8f0fe', border: '#1a73e8', text: '#1967d2' },
    { bg: '#fce8e6', border: '#d93025', text: '#c5221f' },
    { bg: '#e6f4ea', border: '#137333', text: '#0d652d' },
    { bg: '#fef7e0', border: '#f9ab00', text: '#ea8600' },
    { bg: '#e8eaed', border: '#5f6368', text: '#3c4043' },
    { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
  ];
  
  return (
    <div className="google-calendar-style">
      {/* 경고 메시지 제거 - 상단에 통계 정보가 이미 표시되므로 중복 제거 */}
      
      {/* 요일 헤더 */}
      <div className="calendar-weekday-header">
        {weekdayHeaders.map((day, idx) => (
          <div key={idx} className="calendar-weekday-cell">
            {day}
          </div>
        ))}
      </div>
      
      {/* 달력 그리드 */}
      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} className="calendar-week-row">
          {week.map((dateInfo, dayIdx) => {
            if (!dateInfo) {
              return (
                <div 
                  key={dayIdx}
                  className="calendar-day-cell calendar-day-empty"
                />
              );
            }
            
            const { dateKey, day, weekday, sessions } = dateInfo;
            const hasSessions = sessions && Object.keys(sessions).length > 0;
            
            return (
              <div 
                key={dayIdx}
                className={`calendar-day-cell ${hasSessions ? 'calendar-day-has-events' : ''}`}
              >
                <div className="calendar-day-number">
                  {day}
                </div>
                <div className="calendar-day-weekday">
                  {weekday}
                </div>
                
                {hasSessions && (
                  <div className="calendar-day-events">
                    {Object.entries(sessions).map(([timeKey, participantArray], sessionIdx) => {
                      const participantsList = Array.isArray(participantArray) ? participantArray : [participantArray];
                      
                      // 그룹별 색상 결정
                      let color = groupColors[sessionIdx % groupColors.length];
                      if (participantsData && participantsData.length > 0 && participantsList.length > 0) {
                        const firstParticipant = participantsList[0];
                        const participantName = firstParticipant?.name || (typeof firstParticipant === 'string' ? firstParticipant : '');
                        
                        if (participantName) {
                          const participantData = participantsData.find(p => {
                            const pName = p.participant_name || p[nameColumn] || '';
                            return String(pName).trim() === String(participantName).trim();
                          });
                          
                          if (participantData && participantData._assigned_group) {
                            const assignedGroup = participantData._assigned_group;
                            const groupIndex = targetGroups && targetGroups.length > 0
                              ? targetGroups.findIndex(g => {
                                  const gName = typeof g === 'string' ? g : g.name;
                                  return gName === assignedGroup;
                                })
                              : (assignedGroup ? (assignedGroup.charCodeAt(0) % groupColors.length) : -1);
                            if (groupIndex >= 0) {
                              color = groupColors[groupIndex % groupColors.length];
                            }
                          }
                        }
                      }
                      
                      return (
                        <div 
                          key={timeKey} 
                          className="calendar-event-item"
                          style={{
                            borderLeft: `3px solid ${color.border}`,
                            background: color.bg,
                            color: color.text
                          }}
                        >
                          <div className="calendar-event-time">
                            {timeKey}
                          </div>
                          <div className="calendar-event-name">
                            {participantsList.map((p, idx) => {
                              if (!p) return null;
                              const name = p.name || (typeof p === 'string' ? p : '이름 없음');
                              return (
                                <span key={idx}>
                                  {name}
                                  {idx < participantsList.length - 1 && ', '}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const StudySchedulePage = () => {
  const { studySlug } = useParams(); // slug로 변경
  const navigate = useNavigate();
  
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [study, setStudy] = useState(null);
  usePageHeader(
    {
      title: '조사 일정',
      subtitle: study?.name || '조사명',
      showBack: true,
      onBack: () => navigate(`/study/${studySlug}`),
    },
    [study?.name, studySlug]
  );
  const [studyId, setStudyId] = useState(null); // slug로 조회 후 ID 저장
  const [projectName, setProjectName] = useState("");
  const [error, setError] = useState(null);

  // slug로 study 조회
  useEffect(() => {
    const loadStudyBySlug = async () => {
      try {
        console.log('🔍 Study 조회 시작:', { studySlug });
        
        // slug로 연구 조회
        const studyRes = await apiFetch(`/studies/by-slug/${studySlug}`, { method: 'GET' });
        
        console.log('📡 Study 조회 응답:', { ok: studyRes.ok, status: studyRes.status });
        
        if (studyRes.ok) {
          const studyData = await studyRes.json();
          console.log('✅ Study 데이터 로드 성공:', { id: studyData.id, name: studyData.name });
          setStudy(studyData);
          setStudyId(studyData.id); // ID 저장
          
          // 프로젝트 정보 가져오기
          if (studyData.project_id) {
            const projectRes = await apiFetch(`/projects/${studyData.project_id}`, { method: 'GET' });
            if (projectRes.ok) {
              const projectData = await projectRes.json();
              setProjectName(projectData.name || "");
            }
          }
        } else {
          const errorText = await studyRes.text();
          console.error("Study 조회 실패:", { status: studyRes.status, errorText });
          setStudy(null);
          setLoading(false);
        }
      } catch (err) {
        console.error("Study 로드 오류:", err);
        setStudy(null);
        setLoading(false);
      }
    };

    loadStudyBySlug();
  }, [studySlug]);

  // studyId가 설정된 후 일정 로드
  useEffect(() => {
    console.log('🔍 studyId 체크:', { studyId, studySlug });
    
    if (!studyId) {
      console.warn('⚠️ studyId가 없어서 일정을 로드하지 않습니다.');
      return;
    }

    const loadData = async () => {
      try {
        // DB에서 저장된 일정 데이터 조회
        console.log('🔍 일정 데이터 조회 시작:', {
          studyId,
          apiUrl: apiUrl(`/studies/${studyId}/schedule`),
        });
        
        const scheduleRes = await apiFetch(`/studies/${studyId}/schedule`, { method: 'GET' });
        
        console.log('📡 API 응답 상태:', {
          ok: scheduleRes.ok,
          status: scheduleRes.status,
          statusText: scheduleRes.statusText
        });
        
        if (scheduleRes.ok) {
          const result = await scheduleRes.json();
          console.log('📅 DB에서 불러온 일정 데이터 (전체):', result);
          console.log('📅 result.success:', result.success);
          console.log('📅 result.schedule:', result.schedule);
          console.log('📅 result.schedule 타입:', typeof result.schedule);
          console.log('📅 result.schedule이 null인가?', result.schedule === null);
          console.log('📅 result.schedule이 undefined인가?', result.schedule === undefined);
          
          if (result.success && result.schedule) {
            console.log('✅ schedule 설정:', result.schedule);
            setSchedule(result.schedule);
            // schedule이 있지만 final_participants가 없거나 빈 배열인 경우를 체크
            const finalParticipants = Array.isArray(result.schedule?.final_participants)
              ? result.schedule.final_participants
              : (result.schedule?.final_participants?.participants || []);
            
            console.log('📊 final_participants 체크:', {
              finalParticipantsLength: finalParticipants.length,
              finalParticipantsType: typeof result.schedule?.final_participants,
              isArray: Array.isArray(result.schedule?.final_participants)
            });
            
            if (finalParticipants.length === 0) {
              console.warn('⚠️ schedule은 있지만 final_participants가 없거나 빈 배열입니다.');
            }
          } else if (result.success === false) {
            // 백엔드에서 명시적으로 success: false를 반환한 경우
            console.log('📅 백엔드에서 일정이 없다고 응답:', result);
            setSchedule(null);
            setError(result.error || '저장된 일정이 없습니다. Participants 페이지에서 일정을 생성하고 저장해주세요.');
          } else {
            // schedule이 null이거나 없는 경우
            console.log('📅 schedule이 null이거나 없습니다:', result);
            console.log('📅 조건 체크:', {
              'result.success': result.success,
              'result.schedule': result.schedule,
              'result.success && result.schedule': result.success && result.schedule
            });
            setSchedule(null);
            setError('저장된 일정이 없습니다. Participants 페이지에서 일정을 생성하고 저장해주세요.');
          }
        } else {
          const errorText = await scheduleRes.text();
          console.error('❌ 일정 조회 실패:', {
            status: scheduleRes.status,
            statusText: scheduleRes.statusText,
            errorText: errorText
          });
          setSchedule(null);
          setError(`일정 데이터를 불러오는데 실패했습니다 (${scheduleRes.status}). Participants 페이지에서 일정을 생성하고 저장해주세요.`);
        }

        setLoading(false);
      } catch (err) {
        console.error("로드 오류:", err);
        setError('데이터 로드 중 오류가 발생했습니다.');
        setLoading(false);
      }
    };

    loadData();
  }, [studyId]);

  if (loading) {
    return (
      <WorkspaceLayout>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '24px', color: '#1976d2' }} />
          <p style={{ marginTop: '16px', color: '#666' }}>로딩 중...</p>
        </div>
      </WorkspaceLayout>
    );
  }

  // DB에서 불러온 데이터 구조 파싱
  // schedule은 study_schedules 테이블의 한 행
  // final_participants는 참여자 배열만 저장됨 (메타데이터 없음 - 개인정보 보호)
  const finalParticipants = Array.isArray(schedule?.final_participants)
    ? schedule.final_participants
    : (schedule?.final_participants?.participants || []);  // 하위 호환성 (기존 데이터)
  
  console.log('📊 schedule 객체:', schedule);
  console.log('📊 final_participants:', finalParticipants);
  console.log('📊 final_participants 길이:', finalParticipants.length);
  
  // schedule이 있지만 final_participants가 없거나 빈 배열인 경우 체크
  if (schedule && (!schedule.final_participants || finalParticipants.length === 0)) {
    console.warn('⚠️ schedule은 있지만 final_participants가 없거나 빈 배열입니다.');
  }
  
  // final_participants를 optimizedSchedule 형식으로 재구성
  const optimizedSchedule = finalParticipants.length > 0 ? (() => {
    const scheduleAssignments = {};
    let hasAnySchedule = false;
    let processedParticipants = 0;
    let processedSlots = 0;
    let skippedSlots = 0;
    
    console.log('🔄 optimizedSchedule 생성 시작, 참여자 수:', finalParticipants.length);
    
    finalParticipants.forEach((participant, pIdx) => {
      const assignedSchedule = participant.assigned_schedule || [];
      
      console.log(`  참여자 ${pIdx + 1}:`, {
        name: participant.name,
        assignedScheduleLength: assignedSchedule.length,
        assignedSchedule: assignedSchedule
      });
      
      if (assignedSchedule.length > 0) {
        hasAnySchedule = true;
        processedParticipants++;
      }
      
      assignedSchedule.forEach((slot, sIdx) => {
        const dateKey = slot.date;
        const timeSlot = slot.time_slot;
        const weekday = slot.weekday || '';  // DB에 저장된 LLM이 만든 weekday 사용
        
        console.log(`    슬롯 ${sIdx + 1}:`, { dateKey, timeSlot, weekday });
        
        if (!dateKey || !timeSlot) {
          console.warn('⚠️ 잘못된 일정 슬롯:', slot);
          skippedSlots++;
          return;
        }
        
        if (!scheduleAssignments[dateKey]) {
          scheduleAssignments[dateKey] = {
            weekday: weekday  // LLM이 만든 weekday 그대로 사용
          };
        }
        
        if (!scheduleAssignments[dateKey][timeSlot]) {
          scheduleAssignments[dateKey][timeSlot] = [];
        }
        
        // 마스킹된 이름 사용
        scheduleAssignments[dateKey][timeSlot].push(participant.name);
        processedSlots++;
      });
    });
    
    console.log('🗓️ 재구성된 scheduleAssignments:', scheduleAssignments);
    console.log('🗓️ 통계:', {
      hasAnySchedule,
      processedParticipants,
      processedSlots,
      skippedSlots,
      scheduleAssignmentsKeys: Object.keys(scheduleAssignments).length
    });
    
    // scheduleAssignments가 비어있으면 null 반환 (렌더링 안됨)
    if (!hasAnySchedule || Object.keys(scheduleAssignments).length === 0) {
      console.warn('⚠️ 일정 배정이 없습니다. 조건:', {
        hasAnySchedule,
        scheduleAssignmentsKeysLength: Object.keys(scheduleAssignments).length
      });
      return null;
    }
    
    return { schedule_assignments: scheduleAssignments };
  })() : null;
  
  // 참여자 데이터를 CalendarView가 기대하는 형식으로 변환
  const participantsData = finalParticipants.map(p => ({
    participant_id: p.participant_id,
    participant_name: p.name,
    name: p.name,
    _assigned_group: p.assigned_group,
    _selection_status: p.selection_status
  }));
  
  // 그룹 정보 추출
  const targetGroups = [...new Set(finalParticipants.map(p => p.assigned_group).filter(g => g))];
  
  const scheduleData = optimizedSchedule;
  // validationData는 저장하지 않음 (개인정보 보호)
  const validationData = null;
  const csvInfo = { name_column: 'participant_name' };
  
  // 디버깅: 렌더링 조건 확인
  console.log('🔍 렌더링 조건 체크:');
  console.log('  - error:', error);
  console.log('  - schedule:', schedule ? '존재' : 'null');
  console.log('  - schedule?.final_participants:', schedule?.final_participants ? '존재' : 'null');
  console.log('  - finalParticipants.length:', finalParticipants.length);
  console.log('  - optimizedSchedule:', optimizedSchedule);
  console.log('  - scheduleData:', scheduleData);
  console.log('  - scheduleData가 null인가?', scheduleData === null);
  console.log('  - scheduleData가 undefined인가?', scheduleData === undefined);
  
  // 일정 배정 통계 정보 계산 (has_schedule 플래그 사용)
  const totalParticipants = finalParticipants.length;
  const assignedCount = finalParticipants.filter(p => p.has_schedule).length;
  const unassignedCount = totalParticipants - assignedCount;

  // 그룹별 색상 지정
  const groupColors = [
    { bg: '#e8f0fe', border: '#1a73e8', hoverBg: '#d2e3fc' },
    { bg: '#fce8e6', border: '#d93025', hoverBg: '#f9d1cd' },
    { bg: '#e6f4ea', border: '#137333', hoverBg: '#d3eadb' },
    { bg: '#fef7e0', border: '#f9ab00', hoverBg: '#fdedc1' },
    { bg: '#e8eaed', border: '#5f6368', hoverBg: '#dadce0' },
    { bg: '#f3e5f5', border: '#9c27b0', hoverBg: '#e1bee7' },
  ];

  return (
    <WorkspaceLayout>
      {/* Step 5 스타일 컨테이너 */}
      <div className="participants-steps">
        <div className="participants-step-card">
          <div className="participants-step-header open">
            <h2 className="participants-step-title">
              조사 일정
            </h2>
          </div>

          <div className="participants-step-content">
            {error ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: '8px',
                  padding: '20px',
                  color: '#856404'
                }}>
                  <p style={{ marginBottom: '8px', fontWeight: 600 }}>
                    ⚠️ {error}
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    Participants 페이지에서 일정을 생성하고 저장해주세요.
                  </p>
                </div>
              </div>
            ) : schedule && (!schedule.final_participants || finalParticipants.length === 0) ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: '8px',
                  padding: '20px',
                  color: '#856404'
                }}>
                  <p style={{ marginBottom: '8px', fontWeight: 600 }}>
                    ⚠️ 일정 데이터가 비어있습니다
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    저장된 일정 레코드는 있지만 참여자 데이터가 없습니다. Participants 페이지에서 일정을 다시 생성하고 저장해주세요.
                  </p>
                </div>
              </div>
            ) : scheduleData ? (
              <>
                {/* 일정 배정 통계 정보 */}
                {unassignedCount > 0 && (
                  <div style={{
                    background: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                    color: '#856404'
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                      ⚠️ 일정 배정 현황
                    </div>
                    <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                      총 {totalParticipants}명 중 {assignedCount}명 일정 배정 완료
                      {unassignedCount > 0 && (
                        <span style={{ color: '#d32f2f', fontWeight: 600 }}>
                          {' '}/ {unassignedCount}명 일정 미배정
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                {/* 참여자 리스트 */}
                {participantsData.length > 0 && (
                  <div style={{ marginBottom: '32px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '16px'
                    }}>
                      <h4 style={{ margin: 0 }}>선택된 참여자 ({participantsData.length}명)</h4>
                    </div>
                    
                    {/* 그룹별 색상 범례 */}
                    {(() => {
                      const groupsToDisplay = targetGroups.length > 0 
                        ? targetGroups.map(g => typeof g === 'string' ? { name: g } : g)
                        : [...new Set(
                            participantsData.map(p => p._assigned_group).filter(g => g)
                          )].map(name => ({ name }));
                      
                      if (groupsToDisplay.length === 0) return null;
                      
                      return (
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '12px',
                          marginBottom: '16px',
                          padding: '12px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #e0e0e0'
                        }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#666', marginRight: '8px' }}>
                            그룹 구분:
                          </span>
                          {groupsToDisplay.map((group, idx) => {
                            const groupName = typeof group === 'string' ? group : group.name;
                            const groupIndex = targetGroups.length > 0 ? idx : (groupName ? (groupName.charCodeAt(0) % groupColors.length) : -1);
                            const color = groupIndex >= 0 ? groupColors[groupIndex % groupColors.length] : { bg: '#fafafa', border: '#e0e0e0' };
                            
                            return (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                <div style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '4px',
                                  background: color.bg,
                                  border: `2px solid ${color.border}`
                                }} />
                                <span style={{ color: '#333' }}>{groupName}</span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    
                    {/* 참여자 카드 그리드 */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '8px',
                      marginBottom: '24px'
                    }}>
                      {participantsData.map((participant, index) => {
                        const name = participant.participant_name || participant.name || `참여자 ${index + 1}`;
                        const pid = participant.participant_id || '';
                        const assignedGroup = participant._assigned_group || '';
                        
                        const groupIndex = assignedGroup && targetGroups.length > 0
                          ? targetGroups.findIndex(g => {
                              const gName = typeof g === 'string' ? g : g.name;
                              return gName === assignedGroup;
                            })
                          : (assignedGroup ? (assignedGroup.charCodeAt(0) % groupColors.length) : -1);
                        const groupColor = groupIndex >= 0 ? groupColors[groupIndex % groupColors.length] : { bg: '#fafafa', border: '#e0e0e0', hoverBg: '#f0f0f0' };
                        
                        return (
                          <div 
                            key={index} 
                            style={{ 
                              padding: '10px', 
                              border: `1px solid ${groupColor.border}`, 
                              borderRadius: '6px',
                              background: groupColor.bg,
                              fontSize: '13px',
                              transition: 'all 0.2s ease',
                              cursor: 'default'
                            }}
                          >
                            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                              {name}
                            </div>
                            {pid && (
                              <div style={{ fontSize: '11px', color: '#666', fontFamily: 'monospace' }}>
                                ID: {pid}
                              </div>
                            )}
                            {/* 일정 배정 여부 표시 */}
                            {(() => {
                              const originalParticipant = finalParticipants[index];
                              const hasSchedule = originalParticipant?.has_schedule || false;
                              const scheduleCount = originalParticipant?.assigned_schedule?.length || 0;
                              
                              if (!hasSchedule) {
                                return (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    color: '#d32f2f', 
                                    marginTop: '4px',
                                    fontWeight: 500
                                  }}>
                                    ⚠️ 일정 미배정
                                  </div>
                                );
                              } else if (scheduleCount > 0) {
                                return (
                                  <div style={{ 
                                    fontSize: '11px', 
                                    color: '#2e7d32', 
                                    marginTop: '4px'
                                  }}>
                                    ✓ {scheduleCount}개 일정 배정
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 달력 UI */}
                <div>
                  <h5 style={{ marginBottom: '16px' }}>📅 달력으로 보기</h5>
                  <CalendarView
                    optimizedSchedule={scheduleData}
                    participantsData={participantsData}
                    nameColumn={csvInfo.name_column || "participant_name"}
                    targetGroups={targetGroups}
                    validationData={validationData}
                  />
                </div>
              </>
            ) : schedule && finalParticipants.length > 0 ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: '8px',
                  padding: '20px',
                  color: '#856404'
                }}>
                  <p style={{ marginBottom: '8px', fontWeight: 600 }}>
                    ⚠️ 일정 배정이 없습니다
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    저장된 참여자 데이터는 있지만 일정이 배정되지 않았습니다. Participants 페이지에서 일정을 생성하고 저장해주세요.
                  </p>
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fff', borderRadius: '4px', fontSize: '12px', textAlign: 'left' }}>
                    <strong>디버깅 정보:</strong>
                    <pre style={{ margin: '8px 0', fontSize: '11px', overflow: 'auto' }}>
                      {JSON.stringify({
                        scheduleExists: !!schedule,
                        finalParticipantsLength: finalParticipants.length,
                        optimizedSchedule: optimizedSchedule,
                        scheduleData: scheduleData
                      }, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div style={{ 
                  background: '#fff3cd', 
                  border: '1px solid #ffc107', 
                  borderRadius: '8px',
                  padding: '20px',
                  color: '#856404'
                }}>
                  <p style={{ marginBottom: '8px', fontWeight: 600 }}>
                    ⚠️ 일정 데이터 없음
                  </p>
                  <p style={{ fontSize: '14px', color: '#666' }}>
                    일정 데이터가 없습니다. Participants 페이지에서 일정을 생성하고 저장해주세요.
                  </p>
                  <div style={{ marginTop: '16px', padding: '12px', background: '#fff', borderRadius: '4px', fontSize: '12px', textAlign: 'left' }}>
                    <strong>디버깅 정보:</strong>
                    <pre style={{ margin: '8px 0', fontSize: '11px', overflow: 'auto' }}>
                      {JSON.stringify({
                        scheduleExists: !!schedule,
                        schedule: schedule,
                        finalParticipantsLength: finalParticipants.length,
                        optimizedSchedule: optimizedSchedule,
                        scheduleData: scheduleData
                      }, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
};

export default StudySchedulePage;
